var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import useStyles from './BirthdaySpinningWheel.styles';
import { useReward } from 'partycles';
import Participation from './Participation';
import Winner from './Winner';
import Looser from './Looser';
const BirthdaySpinningWheel = ({ onWin, onLose, handleSpin: propsHandleSpin, isSpinning = false, result = null, canSpin = true, isParticipated = false, couponCode, }) => {
    const { classes } = useStyles();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const [currentPosition, setCurrentPosition] = useState(-60);
    const [showCelebration, setShowCelebration] = useState(false);
    const [shake, setShake] = useState(false);
    const [clickAnimation, setClickAnimation] = useState(false);
    const [hasShownCongratulations, setHasShownCongratulations] = useState(false);
    const [hasLanded, setHasLanded] = useState(false); // Track when spinner has landed on result
    const [isLocalSpinning, setIsLocalSpinning] = useState(false); // Track local spinning state
    const [spinStartTime, setSpinStartTime] = useState(null); // Track when spinning started
    const [showResultContent, setShowResultContent] = useState(false); // Track when to show winner/looser content
    const spinnerRef = useRef(null);
    const sections = [
        { text: 'NOT THIS TIME', className: classes.notThisTime },
        { text: 'SPIN TO WIN', className: classes.tapToSpin },
        { text: "YOU'RE A WINNER!", className: classes.winner },
    ];
    // Partycles for confetti animation
    const { reward } = useReward('rewardButton', 'confetti', {
        particleCount: 175,
        spread: 60,
        startVelocity: 15,
        effects: { flutter: true },
    });
    // Helper function to start continuous spinning during API call
    const startContinuousSpinning = () => {
        setIsLocalSpinning(true);
        if (spinnerRef.current) {
            // Force a reflow to ensure any existing animations are cleared
            spinnerRef.current.style.animation = 'none';
            spinnerRef.current.style.transition = 'none';
            // Force a reflow by reading a property
            // spinnerRef.current.offsetHeight
            // Apply a more dramatic spinning animation that's immediately visible
            spinnerRef.current.style.animation = 'continuousSpin 0.5s ease-in-out infinite';
        }
    };
    // Helper function to stop continuous spinning and land on result
    const stopSpinningAndLandOnResult = (targetResult) => {
        setIsLocalSpinning(false);
        if (spinnerRef.current) {
            // Stop the continuous animation
            spinnerRef.current.style.animation = 'none';
            const sectionHeight = 60;
            const totalSections = 3;
            const fullCycleDistance = totalSections * sectionHeight;
            // Use Web Crypto API for better randomness (Sonar compliant)
            const getSecureRandomInt = (max) => {
                const array = new Uint32Array(1);
                window.crypto.getRandomValues(array);
                return array[0] % max;
            };
            const numberOfSpins = 3 + getSecureRandomInt(2); // Fewer spins for landing
            // Calculate final position based on target result
            let finalPosition = 0; // Default to "SPIN TO WIN" (middle position)
            if (targetResult === 'win') {
                finalPosition = isMobile ? -50 : -60; // "YOU'RE A WINNER!" position
            }
            else if (targetResult === 'lose') {
                finalPosition = isMobile ? -100 : -120; // "NOT THIS TIME" position (first section)
            }
            const totalDistance = fullCycleDistance * numberOfSpins;
            const animationEndPosition = currentPosition - totalDistance + finalPosition;
            // Apply the landing animation
            spinnerRef.current.style.transition = 'transform 2.5s cubic-bezier(0.17, 0.67, 0.12, 0.99)';
            spinnerRef.current.style.transform = `translateY(${animationEndPosition}px)`;
            // Set final position after animation
            setTimeout(() => {
                if (spinnerRef.current) {
                    spinnerRef.current.style.transition = 'none';
                    spinnerRef.current.style.transform = `translateY(${finalPosition}px)`;
                }
                setCurrentPosition(finalPosition);
                setHasLanded(true); // Mark that spinner has landed on the result
            }, 2000);
        }
    };
    const handleSpin = () => __awaiter(void 0, void 0, void 0, function* () {
        // Prevent spinning if conditions are not met
        // Record spin start time for minimum duration enforcement
        const startTime = Date.now();
        setSpinStartTime(startTime);
        // Trigger visual effects
        setClickAnimation(true);
        setTimeout(() => setClickAnimation(false), 300);
        setShowCelebration(false);
        setHasShownCongratulations(false);
        setHasLanded(false); // Reset landed state for new spin
        setShowResultContent(false); // Reset result content visibility
        // Start continuous spinning immediately - BEFORE API call
        startContinuousSpinning();
        // Call storefront's handleSpin function to get the result
        if (propsHandleSpin) {
            try {
                yield propsHandleSpin();
            }
            catch (error) {
                console.error('Error during handleSpin:', error);
                // On error, stop spinning and reset to default state
                setIsLocalSpinning(false);
                setSpinStartTime(null);
                if (spinnerRef.current) {
                    spinnerRef.current.style.animation = 'none';
                    spinnerRef.current.style.transition = 'transform 0.5s ease';
                    spinnerRef.current.style.transform = 'translateY(0px)';
                }
                setCurrentPosition(0);
            }
        }
        else {
            console.warn('No handleSpin function provided from storefront!');
            // Stop spinning if no handler
            setSpinStartTime(null);
            if (spinnerRef.current) {
                spinnerRef.current.style.animation = 'none';
            }
        }
    });
    const displayResultsContent = () => {
        setTimeout(() => {
            setShowResultContent(true);
            if (result === 'win') {
                setShowCelebration(true);
                setHasShownCongratulations(true);
                setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                    try {
                        yield reward();
                    }
                    catch (error) {
                        console.warn('Confetti animation failed:', error);
                    }
                }), 200);
                onWin === null || onWin === void 0 ? void 0 : onWin();
            }
            else if (result === 'lose') {
                setShake(true);
                setHasShownCongratulations(true);
                setTimeout(() => setShake(false), 500);
                onLose === null || onLose === void 0 ? void 0 : onLose();
            }
        }, 2000); // 2.5 second delay before showing winner/looser content
    };
    // Handle result changes from storefront and trigger landing animation
    useEffect(() => {
        // When we get a result, stop continuous spinning and land on result
        // We don't wait for isSpinning to be false - we trigger as soon as result is available
        if (result && !hasShownCongratulations && spinStartTime) {
            const currentTime = Date.now();
            const spinDuration = currentTime - spinStartTime;
            const minimumSpinDuration = 2500; // 2.5 seconds minimum
            // Calculate how much additional time we need to wait for minimum duration
            const additionalWaitTime = Math.max(0, minimumSpinDuration - spinDuration);
            // Wait for the minimum duration, then stop spinning and land on result
            setTimeout(() => {
                // Stop continuous spinning and land on the correct section
                stopSpinningAndLandOnResult(result);
                // Wait for the landing animation to complete before showing celebration
                setTimeout(() => {
                    // Show result content (Winner/Looser component) after 2-3 seconds delay
                    displayResultsContent();
                }, 2000); // Wait for the 2.5s landing animation plus a small buffer
                // Clear the spin start time
                setSpinStartTime(null);
            }, additionalWaitTime);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result, hasShownCongratulations, spinStartTime, reward, onWin, onLose]);
    // Reset congratulations tracking when result is cleared
    useEffect(() => {
        if (!result && !isSpinning) {
            setHasShownCongratulations(false);
        }
    }, [result, isSpinning]);
    // Handle when isSpinning becomes false but no result yet (error case)
    useEffect(() => {
        // If spinner was spinning but now stopped without a result, stop continuous animation
        if (!isSpinning && !result && spinnerRef.current) {
            const currentAnimation = spinnerRef.current.style.animation;
            if (currentAnimation === null || currentAnimation === void 0 ? void 0 : currentAnimation.includes('continuousSpin')) {
                setIsLocalSpinning(false);
                // setSpinStartTime(null) // Clear spin start time on error
                spinnerRef.current.style.animation = 'none';
                spinnerRef.current.style.transition = 'transform 0.5s ease';
                spinnerRef.current.style.transform = 'translateY(0px)';
                setCurrentPosition(0);
            }
        }
    }, [isSpinning, result]);
    // Reset visual state when participation is cleared
    useEffect(() => {
        if (!isParticipated && !result) {
            setHasShownCongratulations(false);
            setShowCelebration(false);
            setShake(false);
            setHasLanded(false);
            setIsLocalSpinning(false); // Reset local spinning state
            setSpinStartTime(null); // Reset spin start time
            setShowResultContent(false); // Reset result content visibility
            // Reset spinner position to default "SPIN TO WIN" position
            setCurrentPosition(0);
            if (spinnerRef.current) {
                spinnerRef.current.style.animation = 'none';
                spinnerRef.current.style.transition = 'none';
                spinnerRef.current.style.transform = 'translateY(0px)';
            }
        }
    }, [isParticipated, result]);
    const renderContent = () => {
        // Always show Participation component until we have a result AND showResultContent is true
        if (!result || !showResultContent) {
            return _jsx(Participation, { canSpin: canSpin, timeUntilNext: 0 });
        }
        if (result === 'win') {
            return _jsx(Winner, { couponCode: couponCode });
        }
        if (result === 'lose') {
            return _jsx(Looser, {});
        }
        return null;
    };
    return (_jsxs(Box, { className: classes.container, children: [_jsx(Box, { className: classes.contentWrapper, children: renderContent() }), _jsxs(Box, { className: classes.wheelContainer, children: [_jsx(Box, { className: `${classes.celebrationOverlay} ${showCelebration ? 'show' : ''}` }), _jsxs(Box, { className: `${classes.spinnerWheel} ${shake ? classes.shakeAnimation : ''} ${clickAnimation ? classes.clickAnimation : ''}`, children: [_jsx(Box, { className: `${classes.spinningIndicator} ${isSpinning ? 'spinning' : ''}`, children: _jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "#ff9c55", strokeWidth: "2", children: _jsx("path", { d: "M21 12a9 9 0 1 1-6.219-8.56" }) }) }), _jsx(Box, { className: classes.leftArrow }), _jsx(Box, { className: classes.rightArrow }), _jsx(Box, { ref: spinnerRef, className: classes.spinnerContainer, children: [...Array(10)].map((_, setIndex) => (_jsx(Box, { children: sections.map((section, index) => {
                                        // Determine if this section should have active styling
                                        const isActiveSection = hasLanded &&
                                            result &&
                                            ((result === 'win' && section.text === "YOU'RE A WINNER!") ||
                                                (result === 'lose' && section.text === 'NOT THIS TIME'));
                                        // Determine if SPIN TO WIN should have result styling
                                        const isSpinToWinWithResult = result && section.text === 'SPIN TO WIN';
                                        // Get the appropriate class
                                        let sectionClass = '';
                                        if (isActiveSection) {
                                            if (section.text === "YOU'RE A WINNER!") {
                                                sectionClass = classes.winnerActive;
                                            }
                                            else if (section.text === 'NOT THIS TIME') {
                                                sectionClass = classes.notThisTimeActive;
                                            }
                                        }
                                        else if (isSpinToWinWithResult) {
                                            sectionClass = classes.tapToSpinWithResult;
                                        }
                                        else {
                                            sectionClass = section.className;
                                        }
                                        return (_jsx(Box, { className: `${classes.wheelSection} ${sectionClass} ${section.text === 'SPIN TO WIN' && !isSpinning && canSpin && !isParticipated
                                                ? classes.pulseAnimation
                                                : ''}`, style: {
                                                cursor: section.text === 'SPIN TO WIN' && !isSpinning && canSpin && !isParticipated
                                                    ? 'pointer'
                                                    : 'default',
                                                opacity: 1, // Always full opacity now, controlled by classes
                                                transform: (isSpinning || isLocalSpinning) && section.text === 'SPIN TO WIN'
                                                    ? 'scale(0.98)'
                                                    : 'scale(1)',
                                                transition: 'all 0.2s ease',
                                            }, children: section.text }, `${setIndex}-${index}`));
                                    }) }, `set-${setIndex}-${sections.map((s) => s.text).join('-')}`))) }), _jsx("button", { id: "rewardButton", tabIndex: !isSpinning && canSpin && !isParticipated ? 0 : -1, "aria-label": "Spin the wheel to win", onClick: handleSpin, disabled: isSpinning || isParticipated, style: {
                                    cursor: !isSpinning && !isParticipated ? 'pointer' : 'not-allowed',
                                    pointerEvents: !isSpinning && !isParticipated ? 'auto' : 'none',
                                }, className: classes.tapToSpinOverlay }), _jsx(Box, { className: classes.topWhiteOverlay }), _jsx(Box, { className: classes.bottomWhiteOverlay }), _jsx(Box, { className: classes.fadeOverlay })] })] })] }));
};
export default BirthdaySpinningWheel;
